import {
    Create,
    Datagrid,
    List,
    Edit,
    Show,
    SimpleShowLayout,
    TextField,
    Pagination,
    BooleanField,
    BooleanInput,
    useNotify,
    useCreateSuggestionContext,
    useCreate,
    LinearProgress,
    SelectField,
    SelectInput,
    BulkDeleteButton,
    BulkExportButton,
} from "react-admin";
import { SimpleForm, TextInput } from "react-admin";
import { ConfirmOnRecordButton, LongDateField, ShortDateField } from "./customFields";
import { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, TextField as MuiTextField } from "@mui/material";
import { BulkUpdateForm } from "./bulkUpdate";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

const distributorFilters = [<TextInput source="q" label="Search" alwaysOn />];

const distributorRates = [
    { id: "before_sacem", name: "A: 35% (pay distributor before SACEM)" },
    {
        id: "after_sacem",
        name: "B: 28% (pay distributor after SACEM)",
    },
    {
        id: "no_sacem",
        name: "C: 42% (pay SACEM to distributor)",
    },
];

const DistributorBulkActionButtons = () => (
    <>
        <BulkUpdateForm label="Update distributor rates" icon={<TrendingUpIcon />}>
            <SelectInput source="payment_rate" choices={distributorRates} />
        </BulkUpdateForm>
        <BulkExportButton />
        <BulkDeleteButton mutationMode="pessimistic" />
    </>
);

export const DistributorList = () => (
    <List
        filters={distributorFilters}
        perPage={50}
        pagination={<Pagination rowsPerPageOptions={[25, 50, 100, 200]} />}
        sort={{ field: "created_at", order: "DESC" }}
    >
        <Datagrid rowClick="show" bulkActionButtons={<DistributorBulkActionButtons />}>
            <TextField source="id" />
            <TextField source="name" />
            <TextField source="ddex_party_id" />
            <BooleanField source="is_ddex_supported" />
            <BooleanField source="is_ddex_download_supported" />
            <SelectField source="payment_rate" choices={distributorRates} />
            <ShortDateField source="created_at" label="Added on" />
        </Datagrid>
    </List>
);

export const DistributorShow = () => (
    <Show>
        <SimpleShowLayout>
            <h2>Distributor</h2>
            <TextField source="id" />
            <TextField source="name" />
            <TextField source="ddex_party_id" />
            <TextField source="email" />
            <BooleanField source="is_ddex_supported" />
            <BooleanField source="is_ddex_download_supported" />
            <SelectField source="payment_rate" choices={distributorRates} />
            <LongDateField source="created_at" label="Added on" />
            <ConfirmOnRecordButton
                confirmTitle="Apply messages from this distributor"
                confirmBody="Are you sure you want to apply pending messages from this distributors?"
                url="/cms/distributors/{id}/apply_pending_ddex_messages"
                text="Apply pending DDEX messages"
            />
            <ConfirmOnRecordButton
                confirmTitle="Re-apply messages from this distributor"
                confirmBody="Are you sure you want to re-apply previous messages from this distributors?(Only the latest message per release will be applied)"
                url="/cms/distributors/{id}/reapply_ddex_messages"
                text="Re-apply previous DDEX messages"
                redirectTo="/background_processings"
            />
        </SimpleShowLayout>
    </Show>
);

export const DistributorCreate = () => (
    <Create>
        <SimpleForm>
            <h2>Add Distributor</h2>
            <TextInput source="name" fullWidth />
        </SimpleForm>
    </Create>
);

export const DistributorEdit = () => (
    <Edit mutationMode="pessimistic">
        <SimpleForm>
            <h2>Edit Distributor</h2>

            <TextInput source="name" fullWidth />
            <TextInput source="ddex_party_id" fullWidth label="Party id (Optional)" />
            <BooleanInput source="is_ddex_supported" />
            <BooleanInput source="is_ddex_download_supported" />
            <SelectInput source="payment_rate" choices={distributorRates} />
            <TextInput source="email" fullWidth />
        </SimpleForm>
    </Edit>
);

export const DistributorCreatePopup = () => {
    const { filter, onCancel, onCreate } = useCreateSuggestionContext();
    const [value, setValue] = useState(filter || "");
    const [create, { isLoading }] = useCreate();
    const notify = useNotify();

    const handleSubmit = (event: any) => {
        event.preventDefault();
        const newDistributor = { name: value };

        create("distributors", { data: newDistributor }, { returnPromise: true })
            .then((value: any) => {
                setValue("");
                onCreate(value);
            })
            .catch((reason: any) => {
                notify(reason);
            });
    };

    return (
        <Dialog open onClose={onCancel}>
            {isLoading ? <LinearProgress></LinearProgress> : null}
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <MuiTextField
                        label="Distributor name"
                        value={value}
                        onChange={(event: any) => setValue(event.target.value)}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button type="submit">Save</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
